/**
 * ARIA Dashboard Page
 * Version: 1.0.0
 *
 * ARIA Feature implementations:
 * - ARIA-1: Dashboard tabs (keep dashboard clean, use tabs not inline expansion)
 * - ARIA-2: Jobs Today tile (click navigates to Jobs page filtered to today)
 */

import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, Package, ClipboardCheck } from 'lucide-react';
import { useQuery, gql } from '@apollo/client';
import { useAppSelector, useAppDispatch, setDashboardTab } from '../store';
import type { DashboardStats, Job } from '../types';

// GraphQL query for dashboard stats
const DASHBOARD_STATS_QUERY = gql`
  query DashboardStats {
    dashboardStats {
      jobsToday
      pendingSchedule
      techniciansActive
      waitingOnParts
      pendingDebrief
    }
    jobsToday {
      id
      sytelineOrderNumber
      customerName
      city
      scheduling {
        startTime
      }
      technician {
        fullName
      }
    }
  }
`;

// ARIA-1: Tab configuration
const tabs = [
  { id: 0, name: 'Overview' },
  { id: 1, name: 'Quick Actions' },
  { id: 2, name: 'Recent Activity' },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const activeTab = useAppSelector((state) => state.ui.dashboardActiveTab);

  const { data, loading } = useQuery<{
    dashboardStats: DashboardStats;
    jobsToday: Job[];
  }>(DASHBOARD_STATS_QUERY, {
    pollInterval: 30000, // Refresh every 30 seconds
  });

  const stats = data?.dashboardStats;
  const todayJobs = data?.jobsToday || [];

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Welcome to ARIA</h1>
        <p className="text-gray-600">AI-assisted Scheduling & Automation Platform</p>
      </header>

      {/* ARIA-1: Dashboard tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => dispatch(setDashboardTab(tab.id))}
              className={`tab ${activeTab === tab.id ? 'tab-active' : ''}`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 0 && (
        <OverviewTab
          stats={stats}
          todayJobs={todayJobs}
          loading={loading}
          onJobsTodayClick={() => navigate('/jobs?filter=today')}
        />
      )}

      {activeTab === 1 && <QuickActionsTab />}

      {activeTab === 2 && <RecentActivityTab />}
    </div>
  );
}

// Overview Tab with stat cards
function OverviewTab({
  stats,
  todayJobs,
  loading,
  onJobsTodayClick,
}: {
  stats?: DashboardStats;
  todayJobs: Job[];
  loading: boolean;
  onJobsTodayClick: () => void;
}) {
  const navigate = useNavigate();

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading dashboard...</div>;
  }

  return (
    <>
      {/* Quick stats - ARIA-2: Jobs Today is clickable */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        {/* ARIA-2: Clickable Jobs Today tile */}
        <button
          onClick={onJobsTodayClick}
          className="card p-4 text-left hover:shadow-md hover:border-brand-300 transition-all cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats?.jobsToday || 0}</p>
              <p className="text-sm text-gray-500">Jobs Today</p>
            </div>
          </div>
        </button>

        <StatCard
          icon={<Clock className="w-5 h-5" />}
          label="Pending Schedule"
          value={stats?.pendingSchedule || 0}
          color="yellow"
          onClick={() => navigate('/jobs?status=READY_TO_SCHEDULE')}
        />

        <StatCard
          icon={<Users className="w-5 h-5" />}
          label="Technicians Active"
          value={stats?.techniciansActive || 0}
          color="green"
          onClick={() => navigate('/technicians?available=true')}
        />

        <StatCard
          icon={<Package className="w-5 h-5" />}
          label="Waiting on Parts"
          value={stats?.waitingOnParts || 0}
          color="orange"
          onClick={() => navigate('/pending-parts')}
        />

        <StatCard
          icon={<ClipboardCheck className="w-5 h-5" />}
          label="Pending Debrief"
          value={stats?.pendingDebrief || 0}
          color="purple"
          onClick={() => navigate('/pending-debrief')}
        />
      </div>

      {/* Today's Jobs Preview */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Today's Schedule</h2>
          <button onClick={onJobsTodayClick} className="text-sm text-brand-600 hover:text-brand-700">
            View all
          </button>
        </div>

        {todayJobs.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No jobs scheduled for today</p>
        ) : (
          <div className="space-y-3">
            {todayJobs.slice(0, 5).map((job) => (
              <div
                key={job.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900">{job.sytelineOrderNumber}</p>
                  <p className="text-sm text-gray-600">
                    {job.customerName} - {job.city}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {job.scheduling?.startTime
                      ? new Date(job.scheduling.startTime).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'TBD'}
                  </p>
                  <p className="text-sm text-gray-500">{job.technician?.fullName || 'Unassigned'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// Quick Actions Tab
function QuickActionsTab() {
  const navigate = useNavigate();

  const actions = [
    {
      title: 'Schedule New Job',
      description: 'Book a service appointment',
      href: '/booking',
    },
    {
      title: 'Warranty Lookup',
      description: 'Search by address, customer name, or email',
      href: '/warranty',
    },
    {
      title: 'View Technicians',
      description: 'Check availability and assignments',
      href: '/technicians',
    },
    {
      title: 'Pending Parts',
      description: 'Jobs waiting on parts',
      href: '/pending-parts',
    },
    {
      title: 'Pending Debrief',
      description: 'Jobs needing completion',
      href: '/pending-debrief',
    },
    {
      title: 'In Progress Jobs',
      description: 'Currently active jobs',
      href: '/jobs/in-progress',
    },
  ];

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
      <div className="grid grid-cols-2 gap-4">
        {actions.map((action) => (
          <button
            key={action.href}
            onClick={() => navigate(action.href)}
            className="block p-4 rounded-lg border border-gray-200 hover:border-brand-300 hover:bg-brand-50 transition-colors text-left"
          >
            <h3 className="font-medium text-gray-900">{action.title}</h3>
            <p className="text-sm text-gray-500">{action.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// Recent Activity Tab
function RecentActivityTab() {
  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
      <p className="text-gray-500 text-center py-8">
        Activity feed coming soon. This will show recent job updates, scheduling changes, and
        notifications.
      </p>
    </div>
  );
}

// Stat Card Component
function StatCard({
  icon,
  label,
  value,
  color,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'blue' | 'yellow' | 'green' | 'orange' | 'purple';
  onClick?: () => void;
}) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <button
      onClick={onClick}
      className="card p-4 text-left hover:shadow-md hover:border-brand-300 transition-all cursor-pointer"
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>{icon}</div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
    </button>
  );
}
